import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Input, Select, Tooltip, Table, Spin } from 'antd';
import { QuestionCircleOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import s from './s.module.less';
import { IAdBudgetDisplay, BudgetPeriod, BudgetStatus } from 'types/marketing';
import isNil from 'lodash/isNil';
import useGetOffForProlisting from 'hooks/useGetOffForProlisting';
import { DefaultSpecialtyBid, MentalHealthBid } from 'hooks/marketing/useBudgetInfo';
import useProviderKlarityServiceList from 'hooks/useProviderKlarityServiceList';

interface StrategyTableProps {
    disabled?: boolean;
    data?: IAdBudgetDisplay;
    onChange?: (data: IAdBudgetDisplay) => void;
    onValidationChange?: (errors: { bidError: string; budgetError: string }) => void;
    isWarning?: boolean;
    isOps?: boolean;
    isInGroup?: boolean;
    providerId?: number;
}

const OpsStatusMap = {
    [BudgetStatus.ACTIVE]: 'Active',
    [BudgetStatus.PAUSED]: 'Paused',
    [BudgetStatus.BUDGET_EXHAUSTED]: 'Paused — budget used',
    [BudgetStatus.PAYMENT_OVERDUE]: 'Paused - payment failed',
};

const StrategyTable = ({
    disabled = false,
    data,
    onChange,
    onValidationChange,
    isWarning = false,
    isOps = false,
    isInGroup = false,
    providerId,
}: StrategyTableProps) => {
    const [bidPerBooking, setBidPerBooking] = useState<number>();
    const [budgetType, setBudgetType] = useState<BudgetPeriod>();
    const [budgetAmount, setBudgetAmount] = useState<number>();
    const [bidError, setBidError] = useState('');
    const [budgetError, setBudgetError] = useState('');
    const [isDragging, setIsDragging] = useState(false);
    const sliderRef = useRef<HTMLDivElement>(null);
    const dragBidRef = useRef<number>();
    const [off] = useGetOffForProlisting(isOps);
    const [providerKlarityServiceList, serviceListLoading] = useProviderKlarityServiceList(
        isOps,
        providerId,
        isInGroup,
    );

    const specialties = useMemo(() => {
        if (!providerKlarityServiceList) return [];
        const uniqueSpecialtiesMap = new Map<string, { specialtyName: string; logo: string }>();

        providerKlarityServiceList.forEach((item) => {
            if (item.serviceSpecialtyName && !uniqueSpecialtiesMap.has(item.serviceSpecialtyName)) {
                uniqueSpecialtiesMap.set(item.serviceSpecialtyName, {
                    specialtyName: item.serviceSpecialtyName,
                    logo: item.logo || '',
                });
            }
        });

        return Array.from(uniqueSpecialtiesMap.values());
    }, [providerKlarityServiceList]);

    const DefaultGeneralBid =
        specialties.length === 1 && specialties[0].specialtyName === 'Psychiatric & Mental Health'
            ? MentalHealthBid
            : DefaultSpecialtyBid;

    useEffect(() => {
        if (!isInGroup && serviceListLoading) return;
        // 拖拽期间跳过外部数据同步，避免抖动
        if (isDragging) return;

        const resolvedBid = data?.generalBidAmount || DefaultGeneralBid;
        const resolvedPeriod = data?.budgetPeriod || BudgetPeriod.WEEKLY;
        const resolvedCap = data?.budgetCap || 300;
        setBidPerBooking(resolvedBid);
        setBudgetType(resolvedPeriod);
        setBudgetAmount(resolvedCap);
        // 父级数据缺少有效出价时，把展示用的默认出价同步回父级，
        // guard 只在出价为空时触发，回填后 data.generalBidAmount 变为真值，不会再次触发，避免死循环。
        if (!data?.generalBidAmount && onChange) {
            onChange({
                generalBidAmount: resolvedBid,
                budgetPeriod: resolvedPeriod,
                budgetCap: resolvedCap,
            });
        }
    }, [data, isInGroup, serviceListLoading]);

    // 定义竞争等级的范围和样式
    const competitiveRanges = [
        {
            min: 30,
            max: 59,
            text: 'Less competitive',
            color: '#B91C1C',
            backgroundColor: '#FEF2F2',
            labelPosition: '0',
        },
        { min: 60, max: 79, text: 'Competitive', color: '#B45309', backgroundColor: '#FFFBEB', labelPosition: '20' },
        {
            min: 80,
            max: 199,
            text: 'More competitive',
            color: '#2563EB',
            backgroundColor: '#EFF6FF',
            labelPosition: '40',
        },
        {
            min: 200,
            max: 300,
            text: 'Most competitive',
            color: '#047857',
            backgroundColor: '#ECFDF5',
            labelPosition: '60',
        },
    ];

    // 根据出价获取当前竞争等级
    const getCurrentRange = (bid: number) => {
        if (bid > 300) {
            return competitiveRanges[competitiveRanges.length - 1];
        }
        return competitiveRanges.find((range) => bid >= range.min && bid <= range.max) || competitiveRanges[0];
    };

    // 将出价转换为进度条位置百分比（等分四个区间）
    const bidToPosition = (bid: number) => {
        const clampedBid = Math.max(30, Math.min(300, bid));

        if (clampedBid <= 59) {
            // Less competitive: 30-59 -> 0-25%
            return ((clampedBid - 30) / (59 - 30)) * 25;
        } else if (clampedBid <= 79) {
            // Competitive: 60-79 -> 25-50%
            return 25 + ((clampedBid - 60) / (79 - 60)) * 25;
        } else if (clampedBid <= 199) {
            // More competitive: 80-199 -> 50-75%
            return 50 + ((clampedBid - 80) / (199 - 80)) * 25;
        } else {
            // Most competitive: 200-300 -> 75-100%
            return 75 + ((clampedBid - 200) / (300 - 200)) * 25;
        }
    };

    // 将进度条位置转换为出价（等分四个区间）
    const positionToBid = (position: number) => {
        const clampedPosition = Math.max(0, Math.min(100, position));

        if (clampedPosition <= 25) {
            // 0-25% -> 30-59
            return Math.round(30 + (clampedPosition / 25) * (59 - 30));
        } else if (clampedPosition <= 50) {
            // 25-50% -> 60-79
            return Math.round(60 + ((clampedPosition - 25) / 25) * (79 - 60));
        } else if (clampedPosition <= 75) {
            // 50-75% -> 80-199
            return Math.round(80 + ((clampedPosition - 50) / 25) * (199 - 80));
        } else {
            // 75-100% -> 200-300
            return Math.round(200 + ((clampedPosition - 75) / 25) * (300 - 200));
        }
    };

    const currentRange = getCurrentRange(bidPerBooking !== undefined ? bidPerBooking : DefaultGeneralBid);
    const currentPosition = bidToPosition(bidPerBooking !== undefined ? bidPerBooking : DefaultGeneralBid);

    // 拖动处理函数
    const handleMouseDown = useCallback(
        (e: React.MouseEvent) => {
            if (disabled) return;
            e.preventDefault();
            setIsDragging(true);
        },
        [disabled],
    );

    const handleMouseUp = useCallback(() => {
        setIsDragging(false);
        const currentBid = dragBidRef.current;
        if (onChange && currentBid && currentBid >= 30 && budgetType && budgetAmount && budgetAmount >= currentBid) {
            onChange({
                generalBidAmount: currentBid,
                budgetPeriod: budgetType,
                budgetCap: budgetAmount,
            });
        }
    }, [onChange, budgetType, budgetAmount]);

    // 验证出价
    const validateBid = (value: number) => {
        let error = '';
        if (value < 30) {
            error = 'Amount must be greater than $30.00';
        }
        setBidError(error);
        return error === '';
    };

    // 验证预算
    const validateBudget = (value: number, bid?: number) => {
        let error = '';
        if (value < 30) {
            error = 'Amount must be greater than $30.00';
        } else if (value < (bid ?? bidPerBooking!)) {
            error = 'Your budget must exceed your bid';
        }
        setBudgetError(error);
        return error === '';
    };

    useEffect(() => {
        if (onValidationChange) {
            onValidationChange({ bidError, budgetError });
        }
    }, [bidError, budgetError]);

    const handleMouseMove = useCallback(
        (e: MouseEvent) => {
            if (!isDragging || !sliderRef.current || disabled) return;

            const rect = sliderRef.current.getBoundingClientRect();
            const x = e.clientX - rect.left;
            const percentage = Math.max(0, Math.min(100, (x / rect.width) * 100));
            const newBid = positionToBid(percentage);

            dragBidRef.current = newBid;
            setBidPerBooking(newBid);
            validateBid(newBid);
            if (budgetAmount) {
                validateBudget(budgetAmount, newBid);
            }
        },
        [isDragging, disabled, budgetAmount],
    );

    useEffect(() => {
        if (isDragging) {
            document.addEventListener('mousemove', handleMouseMove);
            document.addEventListener('mouseup', handleMouseUp);
            return () => {
                document.removeEventListener('mousemove', handleMouseMove);
                document.removeEventListener('mouseup', handleMouseUp);
            };
        }
    }, [isDragging, handleMouseMove, handleMouseUp]);

    // 处理出价变化
    const handleBidChange = (value: string) => {
        const numValue = parseFloat(value) || 0;
        setBidPerBooking(numValue);
        const bidErr = validateBid(numValue);
        let budgetErr = true;
        if (budgetAmount) {
            budgetErr = validateBudget(budgetAmount, numValue);
        }
        if (onChange && bidErr && budgetErr) {
            onChange({
                generalBidAmount: numValue,
                budgetPeriod: budgetType!,
                budgetCap: budgetAmount!,
            });
        }
    };

    // 处理预算变化
    const handleBudgetChange = (value: string) => {
        const numValue = parseFloat(value) || 0;
        setBudgetAmount(numValue);
        const budgetErr = validateBudget(numValue);
        if (onChange && !bidError && budgetErr) {
            onChange({
                generalBidAmount: bidPerBooking!,
                budgetPeriod: budgetType!,
                budgetCap: numValue,
            });
        }
    };

    const handleBudgetTypeChange = (value: BudgetPeriod) => {
        setBudgetType(value);
        if (onChange) {
            onChange({
                generalBidAmount: bidPerBooking!,
                budgetPeriod: value,
                budgetCap: budgetAmount!,
            });
        }
    };

    const getStatusClass = (status?: BudgetStatus) => {
        if (!status) return '';
        switch (status) {
            case BudgetStatus.ACTIVE:
                return s.active;
            case BudgetStatus.PAUSED:
                return s.paused;
            case BudgetStatus.BUDGET_EXHAUSTED:
                return s.budgetExhausted;
            case BudgetStatus.PAYMENT_OVERDUE:
                return s.paymentOverdue;
            default:
                return '';
        }
    };

    // 创建表格列配置
    const createColumns = (): ColumnsType<any> => {
        const baseColumns = [
            {
                title: (
                    <div className={s.headerCell}>
                        <span>BID PER BOOKING</span>
                        <Tooltip
                            title="The amount you're willing to pay per patient booking through Pro-Listings."
                            color="#fff"
                            overlayInnerStyle={{ color: '#333', padding: 8 }}
                        >
                            <QuestionCircleOutlined className={s.infoIcon} />
                        </Tooltip>
                    </div>
                ),
                dataIndex: 'bidPerBooking',
                key: 'bidPerBooking',
                width: isOps ? 120 : '25%',
                render: () => (
                    <div className={s.inputContainer}>
                        <Input
                            prefix="$"
                            value={bidPerBooking}
                            onChange={(e) => handleBidChange(e.target.value)}
                            className={`${s.amountInput} ${bidError ? s.error : ''}`}
                            disabled={disabled}
                        />
                        {bidError && <div className={s.errorMessage}>{bidError}</div>}
                    </div>
                ),
            },
            {
                title: (
                    <div className={s.headerCell}>
                        <span>BID RANGE INDICATOR</span>
                        <Tooltip
                            title="The competitiveness is measured by how other same-state providers are bidding in your specialty."
                            color="#fff"
                            overlayInnerStyle={{ color: '#333', padding: 8 }}
                        >
                            <QuestionCircleOutlined className={s.infoIcon} />
                        </Tooltip>
                    </div>
                ),
                dataIndex: 'indicator',
                key: 'indicator',
                width: isOps ? 200 : '25%',
                render: () => (
                    <div className={s.indicatorContainer}>
                        <div
                            className={s.indicatorText}
                            style={{
                                color: currentRange.color,
                                backgroundColor: currentRange.backgroundColor,
                                left: `${currentRange.labelPosition}%`,
                            }}
                        >
                            {currentRange.text}
                        </div>
                        <div className={s.indicatorBar} ref={sliderRef} onMouseDown={handleMouseDown}>
                            <div className={s.segmentBackground} />
                            <div
                                className={s.indicatorProgress}
                                style={{
                                    width: `${currentPosition}%`,
                                }}
                            />
                            <div
                                className={`${s.indicatorHandle} ${isDragging ? s.dragging : ''}`}
                                style={{
                                    left: `${currentPosition}%`,
                                }}
                            />
                        </div>
                    </div>
                ),
            },
            {
                title: (
                    <div className={s.headerCell}>
                        <span>BUDGET TYPE</span>
                        <Tooltip
                            title="Choose how often you want your budget to reset."
                            color="#fff"
                            overlayInnerStyle={{ color: '#333', padding: 8 }}
                        >
                            <QuestionCircleOutlined className={s.infoIcon} />
                        </Tooltip>
                    </div>
                ),
                dataIndex: 'budgetType',
                key: 'budgetType',
                width: isOps ? 120 : '25%',
                render: () => (
                    <Select
                        value={budgetType}
                        onChange={handleBudgetTypeChange}
                        className={s.budgetTypeSelect}
                        disabled={disabled}
                        suffixIcon={
                            <svg width="12" height="8" viewBox="0 0 12 8" fill="none">
                                <path
                                    d="M1 1.5L6 6.5L11 1.5"
                                    stroke="#6B7280"
                                    strokeWidth="1.5"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                />
                            </svg>
                        }
                    >
                        <Select.Option value={BudgetPeriod.WEEKLY}>Weekly</Select.Option>
                        <Select.Option value={BudgetPeriod.MONTHLY}>Monthly</Select.Option>
                    </Select>
                ),
            },
            {
                title: (
                    <div className={s.headerCell}>
                        <span>BUDGET AMOUNT</span>
                        <Tooltip
                            title="The maximum you're willing to spend during the selected time period. Once reached, your Pro-Listings will pause and your budget won't roll over to the next cycle."
                            color="#fff"
                            overlayInnerStyle={{ color: '#333', padding: 16 }}
                        >
                            <QuestionCircleOutlined className={s.infoIcon} />
                        </Tooltip>
                        {isWarning && <div className={s.warningText}>Attention required</div>}
                    </div>
                ),
                dataIndex: 'budgetAmount',
                key: 'budgetAmount',
                width: isOps ? 120 : '25%',
                render: () => (
                    <div className={s.inputContainer}>
                        <Input
                            prefix="$"
                            value={budgetAmount}
                            onChange={(e) => handleBudgetChange(e.target.value)}
                            className={`${s.amountInput} ${budgetError ? s.error : ''}`}
                            disabled={disabled}
                        />
                        {budgetError && <div className={s.errorMessage}>{budgetError}</div>}
                    </div>
                ),
            },
        ];

        // 如果是 ops 模式，添加额外的列
        if (isOps) {
            baseColumns.push(
                {
                    title: (
                        <div className={s.headerCell}>
                            <span>REMAINING BUDGET BALANCE</span>
                            <Tooltip
                                title="Remaining budget available for this campaign."
                                color="#fff"
                                overlayInnerStyle={{ color: '#333', padding: 16 }}
                            >
                                <QuestionCircleOutlined className={s.infoIcon} />
                            </Tooltip>
                        </div>
                    ),
                    dataIndex: 'remainingBudget',
                    key: 'remainingBudget',
                    width: 120,
                    render: () => <span>{isNil(data?.remainingBudget) ? '-' : `$${data?.remainingBudget}`}</span>,
                },
                {
                    title: (
                        <div className={s.headerCell}>
                            <span>FREE CREDIT</span>
                            <Tooltip
                                title={`We offer $${off} credit for free to first-time users.`}
                                color="#fff"
                                overlayInnerStyle={{ color: '#333', padding: 16 }}
                            >
                                <QuestionCircleOutlined className={s.infoIcon} />
                            </Tooltip>
                        </div>
                    ),
                    dataIndex: 'freeCredit',
                    key: 'freeCredit',
                    width: 120,
                    render: () => <span>${isNil(data?.creditBalance?.total) ? 150 : data?.creditBalance?.total}</span>,
                },
                {
                    title: (
                        <div className={s.headerCell}>
                            <span>REMAINING CREDIT BALANCE</span>
                            <Tooltip
                                title="Remaining free credit available for this campaign."
                                color="#fff"
                                overlayInnerStyle={{ color: '#333', padding: 16 }}
                            >
                                <QuestionCircleOutlined className={s.infoIcon} />
                            </Tooltip>
                        </div>
                    ),
                    dataIndex: 'remainingCredit',
                    key: 'remainingCredit',
                    width: 120,
                    render: () => <span>${isNil(data?.creditBalance?.total) ? 300 : data?.creditBalance?.total}</span>,
                },
                {
                    title: (
                        <div className={s.headerCell}>
                            <span>PRO-LISTING BOOKINGS</span>
                            <Tooltip
                                title="Number of bookings from Pro-Listings."
                                color="#fff"
                                overlayInnerStyle={{ color: '#333', padding: 16 }}
                            >
                                <QuestionCircleOutlined className={s.infoIcon} />
                            </Tooltip>
                        </div>
                    ),
                    dataIndex: 'bookings',
                    key: 'bookings',
                    width: 120,
                    render: () => (
                        <span>{isNil(data?.creditBalance?.remaining) ? '-' : data?.creditBalance?.remaining}</span>
                    ),
                },
                {
                    title: (
                        <div className={s.headerCell}>
                            <span>STATUS</span>
                        </div>
                    ),
                    dataIndex: 'status',
                    key: 'status',
                    width: 100,
                    render: () => (
                        <span className={`${s.statusTag} ${getStatusClass(data?.status as BudgetStatus)}`}>
                            {OpsStatusMap[data?.status as BudgetStatus] || 'Inactive'}
                        </span>
                    ),
                },
                {
                    title: (
                        <div className={s.headerCell}>
                            <span>LAST MODIFIED</span>
                        </div>
                    ),
                    dataIndex: 'lastModified',
                    key: 'lastModified',
                    width: 120,
                    render: () => <span>{data?.updatedAt || '-'}</span>,
                },
                {
                    title: (
                        <div className={s.headerCell}>
                            <span>LAST MODIFIED BY</span>
                        </div>
                    ),
                    dataIndex: 'lastModifiedBy',
                    key: 'lastModifiedBy',
                    width: 150,
                    render: () => <span>{data?.updatedByName || '-'}</span>,
                },
            );
        }

        // Ops group pool view doesn't use the per-provider credit / bookings columns.
        if (isOps && isInGroup) {
            return baseColumns.filter((c) => !['freeCredit', 'remainingCredit', 'bookings'].includes(c.key as string));
        }

        return baseColumns;
    };

    // 表格数据源 - 只需要一行数据
    const dataSource = [{ key: 'strategy' }];

    return (
        <Spin spinning={!isInGroup && serviceListLoading}>
            <div
                className={`${s.tableContainer} ${disabled ? s.disabled : ''} ${isWarning ? s.warning : ''} ${isOps ? s.opsTableContainer : ''}`}
            >
                <Table
                    columns={createColumns()}
                    dataSource={dataSource}
                    pagination={false}
                    showHeader
                    scroll={isOps ? { x: 'max-content' } : undefined}
                    bordered={false}
                />
            </div>
        </Spin>
    );
};

export default StrategyTable;
